import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from './Link.tsx';
import { fmtTime, useCtx, useLocation } from '../lib.ts';

type Status = 'ONLINE' | 'BUSY' | 'AWAY' | 'OFFLINE';
interface Member {
  userId: string;
  displayName: string;
  role: string;
  status: Status;
  manual: 'AUTO' | 'BUSY' | 'AWAY';
  statusText: string | null;
  lastSeenAt: string | null;
  unread: number;
}
interface TeamState {
  me: string;
  canReadLog: boolean;
  members: Member[];
  channels: { channelId: string; name: string; topic: string | null; unread: number }[];
  unreadTotal: number;
  myOpenTasks: number;
  myUrgentTasks: number;
}
interface Msg {
  id: number;
  author: string;
  body: string | null;
  kind: 'USER' | 'SYSTEM';
  createdAt: string;
  editedAt: string | null;
  deleted: boolean;
}
interface Task {
  id: number;
  title: string;
  details: string | null;
  assignee: string | null;
  createdBy: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  status: 'OPEN' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
  dueAt: string | null;
  link: { kind: string; id: string } | null;
  createdAt: string;
  updatedAt: string;
}
type Server = NonNullable<ReturnType<typeof useCtx>['backend']['server']>;

const STATUS_LABEL: Record<Status, string> = { ONLINE: 'Online', BUSY: 'Busy', AWAY: 'Away', OFFLINE: 'Offline' };
const IDLE_MS = 10 * 60_000;

const SOUND_KEY = 'urbanrats.team.sound.v1';
export const teamSoundOn = () => {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
};
let audio: AudioContext | null = null;
/** A short soft "tick-tock" for a new team message (Web Audio; plays after the first click on the page). */
export function teamClick() {
  if (!teamSoundOn()) return;
  try {
    audio ??= new AudioContext();
    if (audio.state === 'suspended') void audio.resume();
    const t = audio.currentTime;
    for (const [i, f] of [1320, 990].entries()) {
      const o = audio.createOscillator();
      const g = audio.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t + i * 0.09);
      g.gain.exponentialRampToValueAtTime(0.18, t + i * 0.09 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.09 + 0.08);
      o.connect(g).connect(audio.destination);
      o.start(t + i * 0.09);
      o.stop(t + i * 0.09 + 0.1);
    }
  } catch {
    /* no audio available */
  }
}

/**
 * Presence heartbeat for the whole portal (mounted once in the app shell): every 30 s while the portal is
 * open, with `idle` after 10 minutes without mouse or keyboard input. The team state (unread messages, open
 * tasks) is read every 8 s; more unread than before plays the message sound and shows in the tab title.
 */
export function useTeamHeartbeat(server: Server | undefined, enabled: boolean): TeamState | null {
  const [state, setState] = useState<TeamState | null>(null);
  const lastInput = useRef(0);
  const lastUnread = useRef<number | null>(null);
  useEffect(() => {
    if (!server || !enabled) return;
    lastInput.current = Date.now();
    const mark = () => (lastInput.current = Date.now());
    const events = ['mousemove', 'keydown', 'pointerdown', 'wheel', 'touchstart'] as const;
    for (const e of events) addEventListener(e, mark, { passive: true });
    let live = true;
    const read = async () => {
      const r = await server.get<TeamState>('/v1/admin/team/state');
      if (!live || !r.ok) return;
      const n = r.value.unreadTotal + r.value.myOpenTasks;
      if (lastUnread.current !== null && n > lastUnread.current) teamClick();
      lastUnread.current = n;
      const base = document.title.replace(/^\(\d+\) /, '');
      document.title = r.value.unreadTotal > 0 ? `(${r.value.unreadTotal}) ${base}` : base;
      setState(r.value);
    };
    const beat = async () => {
      if (document.visibilityState === 'hidden' && Date.now() - lastInput.current > IDLE_MS) return;
      await server.post('/v1/admin/team/heartbeat', { idle: Date.now() - lastInput.current > IDLE_MS });
    };
    const first = setTimeout(() => (void beat(), void read()), 0);
    const hb = setInterval(() => void beat(), 30_000);
    const st = setInterval(() => void read(), 8000);
    return () => {
      live = false;
      clearTimeout(first);
      clearInterval(hb);
      clearInterval(st);
      for (const e of events) removeEventListener(e, mark);
    };
  }, [server, enabled]);
  return state;
}

export function StatusDot({ status, title }: { status: Status; title?: string }) {
  return <span className={`tm-dot tm-${status}`} title={title ?? STATUS_LABEL[status]} aria-label={STATUS_LABEL[status]} role="img" />;
}

/** Turns round, camera, bet ids and @mentions in a message into links/highlights. */
function Rich({ text, me }: { text: string; me: string }) {
  const parts: ReactNode[] = [];
  const re = /(demo-r\d{4,}|r_[A-Za-z0-9-]{6,}|argus:[A-Za-z0-9_:.-]+|@[a-z0-9._-]{2,40}|https:\/\/[^\s]+)/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    const t = m[0];
    if (m.index! > last) parts.push(text.slice(last, m.index));
    if (t.startsWith('@')) parts.push(<mark key={m.index} className={t.slice(1) === me ? 'tm-me' : ''}>{t}</mark>);
    else if (t.startsWith('argus:'))
      parts.push(
        <Link key={m.index} to={`/cameras/${encodeURIComponent(t)}`}>
          {t.replace(/^argus:opencctv_[^_]+_/, '')}
        </Link>,
      );
    else if (t.startsWith('https://'))
      parts.push(
        <a key={m.index} href={t} target="_blank" rel="noreferrer noopener">
          {t}
        </a>,
      );
    else
      parts.push(
        <Link key={m.index} to={`/rounds/${encodeURIComponent(t)}`}>
          {t}
        </Link>,
      );
    last = m.index! + t.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

const convName = (conv: string, st: TeamState) => {
  if (conv.startsWith('ch:')) return `# ${st.channels.find((c) => `ch:${c.channelId}` === conv)?.name ?? conv.slice(3)}`;
  const id = conv.slice(3);
  return st.members.find((m) => m.userId === id)?.displayName ?? id;
};

/**
 * The team area: who is online and in which role, channels and direct messages, tasks between members, and
 * (admins only) the full log. `compact` is the version on the overview page.
 */
export function TeamPanel({ compact = false }: { compact?: boolean }) {
  const { backend } = useCtx();
  const server = backend.server;
  const [st, setSt] = useState<TeamState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [conv, setConv] = useState('ch:operations');
  const [view, setView] = useState<'chat' | 'tasks' | 'log'>('chat');
  // A notification link opens one conversation (?conv=…) or the tasks (?tab=tasks).
  const { search } = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(search);
    const c = q.get('conv');
    const t0 = setTimeout(() => {
      if (c && /^(ch|dm):[\w.-]+$/.test(c)) {
        setConv(c);
        setView('chat');
      } else if (q.get('tab') === 'tasks') setView('tasks');
    }, 0);
    return () => clearTimeout(t0);
  }, [search]);
  const loadState = useCallback(async () => {
    if (!server) return;
    const r = await server.get<TeamState>('/v1/admin/team/state');
    if (r.ok) {
      setSt(r.value);
      setErr(null);
    } else setErr(r.error);
  }, [server]);
  useEffect(() => {
    const first = setTimeout(() => void loadState(), 0);
    const t = setInterval(() => void loadState(), 8000);
    return () => (clearTimeout(first), clearInterval(t));
  }, [loadState]);
  if (!server) return null;
  if (!st) return <p className="empty">{err ?? 'Loading the team…'}</p>;
  return (
    <section className={`tm ${compact ? 'compact' : ''}`} data-testid="team-panel">
      <aside className="tm-side">
        <MyStatus st={st} server={server} reload={loadState} />
        <h4>Channels</h4>
        <ul className="tm-convs">
          {st.channels.map((c) => (
            <li key={c.channelId}>
              <button type="button" className={conv === `ch:${c.channelId}` && view === 'chat' ? 'on' : ''} onClick={() => (setConv(`ch:${c.channelId}`), setView('chat'))} title={c.topic ?? undefined}>
                <span># {c.name}</span>
                {c.unread > 0 && <b className="tm-badge">{c.unread}</b>}
              </button>
            </li>
          ))}
        </ul>
        <h4>
          Team <span className="muted">· {st.members.filter((m) => m.status !== 'OFFLINE').length} online</span>
        </h4>
        <ul className="tm-convs tm-people">
          {st.members.map((m) => (
            <li key={m.userId}>
              <button type="button" disabled={m.userId === st.me} className={conv === `dm:${m.userId}` && view === 'chat' ? 'on' : ''} onClick={() => (setConv(`dm:${m.userId}`), setView('chat'))} data-testid={`tm-member-${m.userId}`}>
                <StatusDot status={m.status} title={`${STATUS_LABEL[m.status]}${m.statusText ? ` — ${m.statusText}` : ''}${m.status === 'OFFLINE' && m.lastSeenAt ? ` · last seen ${fmtTime(m.lastSeenAt)}` : ''}`} />
                <span className="tm-person">
                  <span>
                    {m.displayName}
                    {m.userId === st.me ? ' (you)' : ''}
                  </span>
                  <small className="muted">
                    {m.role.replace(/_/g, ' ').toLowerCase()}
                    {m.statusText ? ` · ${m.statusText}` : ''}
                  </small>
                </span>
                {m.unread > 0 && <b className="tm-badge">{m.unread}</b>}
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <div className="tm-main">
        <nav className="tm-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={view === 'chat'} className={view === 'chat' ? 'on' : ''} onClick={() => setView('chat')}>
            {convName(conv, st)}
          </button>
          <button type="button" role="tab" aria-selected={view === 'tasks'} className={view === 'tasks' ? 'on' : ''} onClick={() => setView('tasks')} data-testid="tm-tab-tasks">
            Tasks {st.myOpenTasks > 0 && <b className={`tm-badge ${st.myUrgentTasks ? 'urgent' : ''}`}>{st.myOpenTasks}</b>}
          </button>
          {st.canReadLog && (
            <button type="button" role="tab" aria-selected={view === 'log'} className={view === 'log' ? 'on' : ''} onClick={() => setView('log')} data-testid="tm-tab-log">
              Log (admin)
            </button>
          )}
        </nav>
        {view === 'chat' && <Chat key={conv} conv={conv} st={st} server={server} onRead={loadState} compact={compact} />}
        {view === 'tasks' && <Tasks st={st} server={server} reload={loadState} />}
        {view === 'log' && st.canReadLog && <TeamLog st={st} server={server} />}
      </div>
    </section>
  );
}

function MyStatus({ st, server, reload }: { st: TeamState; server: Server; reload: () => Promise<void> }) {
  const me = st.members.find((m) => m.userId === st.me);
  const [text, setText] = useState(me?.statusText ?? '');
  if (!me) return null;
  const set = async (manual: string, statusText = text) => {
    await server.post('/v1/admin/team/presence', { manual, statusText });
    await reload();
  };
  return (
    <div className="tm-me-box">
      <div className="tm-me-row">
        <StatusDot status={me.status} />
        <strong>{me.displayName}</strong>
      </div>
      <div className="tm-status-btns" role="group" aria-label="Your status">
        {(['AUTO', 'BUSY', 'AWAY'] as const).map((s) => (
          <button key={s} type="button" className={me.manual === s ? 'on' : ''} onClick={() => void set(s)} data-testid={`tm-status-${s}`}>
            {s === 'AUTO' ? 'Online' : s === 'BUSY' ? 'Busy' : 'Away'}
          </button>
        ))}
      </div>
      <SoundToggle />
      <input
        className="tm-status-text"
        value={text}
        maxLength={80}
        placeholder="What are you on? (optional)"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void set(me.manual, text);
        }}
        onBlur={() => {
          if ((me.statusText ?? '') !== text) void set(me.manual, text);
        }}
      />
    </div>
  );
}

function Chat({ conv, st, server, onRead, compact }: { conv: string; st: TeamState; server: Server; onRead: () => Promise<void>; compact: boolean }) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: number; body: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const lastId = useRef(0);
  const names = useMemo(() => new Map(st.members.map((m) => [m.userId, m.displayName])), [st.members]);
  const load = useCallback(
    async (full = false) => {
      const r = await server.get<{ messages: Msg[] }>(`/v1/admin/team/messages?conv=${encodeURIComponent(conv)}${full ? '' : `&after=${lastId.current}`}`);
      if (!r.ok) return setErr(r.error);
      setErr(null);
      if (full) setMsgs(r.value.messages);
      else if (r.value.messages.length) {
        // A new message from someone else in the open conversation: the same sound.
        if (lastId.current > 0 && r.value.messages.some((m) => m.author !== st.me && m.id > lastId.current)) teamClick();
        setMsgs((old) => [...old, ...r.value.messages.filter((m) => !old.some((o) => o.id === m.id))]);
      }
      const last = r.value.messages.at(-1);
      if (last) {
        lastId.current = Math.max(lastId.current, last.id);
        void onRead();
      }
    },
    [server, conv, onRead, st.me],
  );
  useEffect(() => {
    lastId.current = 0;
    const first = setTimeout(() => void load(true), 0);
    const t = setInterval(() => void load(), 3000);
    // Edits and deletions of older messages: a full refresh every 30 s.
    const full = setInterval(() => void load(true), 30_000);
    return () => (clearTimeout(first), clearInterval(t), clearInterval(full));
  }, [load]);
  useEffect(() => {
    const el = box.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs.length]);
  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    const r = await server.post('/v1/admin/team/messages', { conv, body });
    if (!r.ok) return setErr(r.error);
    setDraft('');
    await load();
  };
  return (
    <div className="tm-chat">
      <div className={`tm-msgs ${compact ? 'compact' : ''}`} ref={box} aria-live="polite" data-testid="tm-messages">
        {msgs.length === 0 && <p className="muted tm-empty">No messages yet. Round ids (demo-r000123) and camera ids become links; @name mentions someone.</p>}
        {msgs.map((m, i) => {
          const prev = msgs[i - 1];
          const grouped = prev && prev.author === m.author && prev.kind === m.kind && Date.parse(m.createdAt) - Date.parse(prev.createdAt) < 5 * 60_000;
          const mine = m.author === st.me;
          return (
            <div key={m.id} className={`tm-msg ${m.kind === 'SYSTEM' ? 'sys' : ''} ${mine ? 'mine' : ''} ${grouped ? 'grouped' : ''}`}>
              {!grouped && (
                <div className="tm-msg-head">
                  <strong>{names.get(m.author) ?? m.author}</strong>
                  <time className="muted">{fmtTime(m.createdAt)}</time>
                </div>
              )}
              {editing?.id === m.id ? (
                <form
                  className="tm-edit"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const r = await server.post(`/v1/admin/team/messages/${m.id}/edit`, { body: editing.body });
                    if (!r.ok) return setErr(r.error);
                    setEditing(null);
                    await load(true);
                  }}
                >
                  <input value={editing.body} onChange={(e) => setEditing({ id: m.id, body: e.target.value })} autoFocus />
                  <button type="submit" className="btn">
                    Save
                  </button>
                  <button type="button" className="btn ghost" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </form>
              ) : (
                <div className="tm-body">
                  {m.deleted ? <em className="muted">message deleted</em> : <Rich text={m.body ?? ''} me={st.me} />}
                  {m.editedAt && !m.deleted && <small className="muted"> (edited)</small>}
                  {mine && !m.deleted && m.kind === 'USER' && (
                    <span className="tm-msg-actions">
                      <button type="button" onClick={() => setEditing({ id: m.id, body: m.body ?? '' })}>
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!confirm('Delete this message? (Admins can still see it in the log.)')) return;
                          await server.post(`/v1/admin/team/messages/${m.id}/delete`, {});
                          await load(true);
                        }}
                      >
                        Delete
                      </button>
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {err && <p className="op-msg bad">{err}</p>}
      <form
        className="tm-compose"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <textarea
          value={draft}
          rows={compact ? 1 : 2}
          maxLength={4000}
          placeholder={`Message ${convName(conv, st)} — Enter to send, Shift+Enter for a new line`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          data-testid="tm-compose"
        />
        <button type="submit" className="btn primary" disabled={!draft.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}

const PRIO_CLASS: Record<Task['priority'], string> = { LOW: '', NORMAL: '', HIGH: 'st-PENDING', URGENT: 'st-FAILED' };

function Tasks({ st, server, reload }: { st: TeamState; server: Server; reload: () => Promise<void> }) {
  const { nowMs } = useCtx();
  const [filter, setFilter] = useState<'mine' | 'open' | 'created' | 'done'>('mine');
  const [tasks, setTasks] = useState<Task[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const names = new Map(st.members.map((m) => [m.userId, m.displayName]));
  const load = useCallback(async () => {
    const r = await server.get<{ tasks: Task[] }>(`/v1/admin/team/tasks?filter=${filter}`);
    if (r.ok) setTasks(r.value.tasks);
    else setErr(r.error);
  }, [server, filter]);
  useEffect(() => {
    const first = setTimeout(() => void load(), 0);
    const t = setInterval(() => void load(), 10_000);
    return () => (clearTimeout(first), clearInterval(t));
  }, [load]);
  const update = async (id: number, body: Record<string, unknown>) => {
    const r = await server.post(`/v1/admin/team/tasks/${id}`, body);
    if (!r.ok) return setErr(r.error);
    await load();
    await reload();
  };
  const linkTo = (l: Task['link']) => (!l ? null : l.kind === 'ROUND' ? `/rounds/${encodeURIComponent(l.id)}` : l.kind === 'CAMERA' ? `/cameras/${encodeURIComponent(l.id)}` : l.kind === 'PLAYER' ? `/players/${encodeURIComponent(l.id)}` : l.kind === 'BET' ? `/bets/${encodeURIComponent(l.id)}` : '/operators');
  return (
    <div className="tm-tasks">
      <div className="tm-task-bar">
        <div className="tm-status-btns" role="group" aria-label="Task filter">
          {(['mine', 'open', 'created', 'done'] as const).map((f) => (
            <button key={f} type="button" className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
              {f === 'mine' ? 'Assigned to me' : f === 'open' ? 'All open' : f === 'created' ? 'Created by me' : 'Done (14 days)'}
            </button>
          ))}
        </div>
        <button type="button" className="btn primary" onClick={() => setAdding((v) => !v)} data-testid="tm-task-new">
          {adding ? 'Close' : '+ New task'}
        </button>
      </div>
      {adding && (
        <NewTask
          st={st}
          server={server}
          done={async () => {
            setAdding(false);
            await load();
            await reload();
          }}
        />
      )}
      {err && <p className="op-msg bad">{err}</p>}
      {!tasks ? (
        <p className="muted">Loading…</p>
      ) : tasks.length === 0 ? (
        <p className="empty">No tasks here.</p>
      ) : (
        <ul className="tm-task-list">
          {tasks.map((t) => {
            const overdue = t.dueAt && Date.parse(t.dueAt) < nowMs && (t.status === 'OPEN' || t.status === 'IN_PROGRESS');
            const to = linkTo(t.link);
            return (
              <li key={t.id} className={`tm-task ${t.status}`} data-testid={`tm-task-${t.id}`}>
                <div className="tm-task-top">
                  <strong>
                    #{t.id} {t.title}
                  </strong>
                  <span className="tm-task-tags">
                    {t.priority !== 'NORMAL' && <span className={`tag ${PRIO_CLASS[t.priority]}`}>{t.priority}</span>}
                    <span className={`tag st-${t.status === 'DONE' ? 'SETTLED' : t.status === 'IN_PROGRESS' ? 'ACCEPTED' : t.status === 'CANCELLED' ? 'VOIDED' : 'PENDING'}`}>{t.status.replace('_', ' ')}</span>
                  </span>
                </div>
                {t.details && <p className="tm-task-details">{t.details}</p>}
                <div className="tm-task-meta muted">
                  <span>
                    → {t.assignee ? names.get(t.assignee) ?? t.assignee : 'unassigned'} · from {names.get(t.createdBy) ?? t.createdBy}
                  </span>
                  {t.dueAt && <span className={overdue ? 'tm-overdue' : ''}>due {fmtTime(t.dueAt)}</span>}
                  {to && t.link && (
                    <Link to={to}>
                      {t.link.kind.toLowerCase()} {t.link.id.replace(/^argus:opencctv_[^_]+_/, '')}
                    </Link>
                  )}
                </div>
                {(t.status === 'OPEN' || t.status === 'IN_PROGRESS') && (
                  <div className="tm-task-actions">
                    {t.status === 'OPEN' && (
                      <button type="button" className="btn" onClick={() => void update(t.id, { status: 'IN_PROGRESS', ...(t.assignee ? {} : { assignee: st.me }) })}>
                        Start
                      </button>
                    )}
                    <button type="button" className="btn primary" onClick={() => void update(t.id, { status: 'DONE' })}>
                      Done
                    </button>
                    <select value={t.assignee ?? ''} onChange={(e) => void update(t.id, { assignee: e.target.value || null })} aria-label="Assignee">
                      <option value="">unassigned</option>
                      {st.members.map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.displayName}
                        </option>
                      ))}
                    </select>
                    <button type="button" className="btn ghost" onClick={() => confirm(`Cancel task #${t.id}?`) && void update(t.id, { status: 'CANCELLED' })}>
                      Cancel
                    </button>
                  </div>
                )}
                {(t.status === 'DONE' || t.status === 'CANCELLED') && (
                  <div className="tm-task-actions">
                    <button type="button" className="btn ghost" onClick={() => void update(t.id, { status: 'OPEN' })}>
                      Reopen
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function NewTask({ st, server, done }: { st: TeamState; server: Server; done: () => Promise<void> }) {
  const [f, setF] = useState({ title: '', details: '', assignee: '', priority: 'NORMAL', dueAt: '', linkKind: '', linkId: '' });
  const [err, setErr] = useState<string | null>(null);
  return (
    <form
      className="tm-new-task"
      onSubmit={async (e) => {
        e.preventDefault();
        const r = await server.post('/v1/admin/team/tasks', { ...f, dueAt: f.dueAt ? new Date(f.dueAt).toISOString() : null, assignee: f.assignee || null, linkKind: f.linkKind || null, linkId: f.linkKind ? f.linkId : null });
        if (!r.ok) return setErr(r.error);
        await done();
      }}
    >
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="What needs to be done?" maxLength={200} autoFocus data-testid="tm-task-title" />
      <textarea value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} placeholder="Details (optional)" rows={2} />
      <div className="tm-new-row">
        <select value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })} aria-label="Assignee" data-testid="tm-task-assignee">
          <option value="">Assign to…</option>
          {st.members.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.displayName} · {STATUS_LABEL[m.status]}
            </option>
          ))}
        </select>
        <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} aria-label="Priority">
          <option value="LOW">Low</option>
          <option value="NORMAL">Normal</option>
          <option value="HIGH">High</option>
          <option value="URGENT">Urgent</option>
        </select>
        <input type="datetime-local" value={f.dueAt} onChange={(e) => setF({ ...f, dueAt: e.target.value })} aria-label="Due" />
        <select value={f.linkKind} onChange={(e) => setF({ ...f, linkKind: e.target.value })} aria-label="Link to">
          <option value="">No link</option>
          <option value="ROUND">Round</option>
          <option value="CAMERA">Camera</option>
          <option value="PLAYER">Player</option>
          <option value="BET">Bet</option>
          <option value="OPERATOR">Operator</option>
        </select>
        {f.linkKind && <input value={f.linkId} onChange={(e) => setF({ ...f, linkId: e.target.value })} placeholder={f.linkKind === 'ROUND' ? 'demo-r000123' : f.linkKind === 'CAMERA' ? 'argus:…' : 'id'} aria-label="Linked id" />}
      </div>
      <div className="op-actions">
        <button type="submit" className="btn primary" disabled={f.title.trim().length < 2 || (!!f.linkKind && !f.linkId.trim())} data-testid="tm-task-save">
          Create task
        </button>
        {err && <span className="op-msg bad">{err}</span>}
      </div>
    </form>
  );
}

interface LogEntry {
  seq: number;
  at: string;
  actor: string;
  kind: string;
  conv: string | null;
  messageId: number | null;
  taskId: number | null;
  detail: Record<string, unknown>;
}

function TeamLog({ st, server }: { st: TeamState; server: Server }) {
  const [rows, setRows] = useState<LogEntry[] | null>(null);
  const [f, setF] = useState({ actor: '', kind: '', search: '' });
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    const q = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
    const r = await server.get<{ entries: LogEntry[] }>(`/v1/admin/team/log?${q}`);
    if (r.ok) setRows(r.value.entries);
    else setErr(r.error);
  }, [server, f]);
  useEffect(() => {
    const t = setTimeout(() => void load(), 0);
    return () => clearTimeout(t);
  }, [load]);
  const where = (e: LogEntry) => (e.conv ? convName(e.conv.startsWith('dm:') ? `dm:${e.conv.slice(3).split('|').filter((x) => x !== e.actor)[0] ?? ''}` : e.conv, st) : e.taskId ? `task #${e.taskId}` : '');
  const what = (e: LogEntry) => {
    const d = e.detail;
    if (e.kind === 'MESSAGE') return String(d.body ?? '');
    if (e.kind === 'MESSAGE_EDIT') return `“${String(d.before)}” → “${String(d.after)}”`;
    if (e.kind === 'MESSAGE_DELETE') return `deleted: “${String(d.body)}”`;
    if (e.kind === 'PRESENCE') return `status ${String(d.manual)}${d.statusText ? ` · ${String(d.statusText)}` : ''}`;
    if (e.kind === 'TASK_CREATE') return `created “${String(d.title)}”${d.assignee ? ` → ${String(d.assignee)}` : ''}`;
    return Object.entries(d)
      .map(([k, v]) => `${k}: ${String((v as { from: unknown }).from ?? '—')} → ${String((v as { to: unknown }).to ?? '—')}`)
      .join(' · ');
  };
  return (
    <div className="tm-log" data-testid="tm-log">
      <p className="note">Everything said and done in the team area, including direct messages and the text of edited or deleted messages. Append-only; visible to admins only.</p>
      <div className="tm-new-row">
        <select value={f.actor} onChange={(e) => setF({ ...f, actor: e.target.value })} aria-label="Person">
          <option value="">Everyone</option>
          {st.members.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.displayName}
            </option>
          ))}
        </select>
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} aria-label="Kind">
          <option value="">All events</option>
          {['MESSAGE', 'MESSAGE_EDIT', 'MESSAGE_DELETE', 'TASK_CREATE', 'TASK_UPDATE', 'PRESENCE'].map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
        <input value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} placeholder="Search text" aria-label="Search" />
      </div>
      {err && <p className="op-msg bad">{err}</p>}
      {!rows ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Who</th>
                <th>Event</th>
                <th>Where</th>
                <th>What</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.seq}>
                  <td className="muted">{fmtTime(e.at)}</td>
                  <td className="mono">{e.actor}</td>
                  <td>
                    <span className="tag">{e.kind}</span>
                  </td>
                  <td>{where(e)}</td>
                  <td className="tm-log-what">{what(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SoundToggle() {
  const [on, setOn] = useState(teamSoundOn);
  return (
    <label className="tm-sound">
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => {
          setOn(e.target.checked);
          try {
            localStorage.setItem(SOUND_KEY, e.target.checked ? 'on' : 'off');
          } catch {
            /* per-browser convenience */
          }
          if (e.target.checked) teamClick();
        }}
      />{' '}
      Message sound
    </label>
  );
}
